import React, { useState, useEffect, useCallback } from 'react';
import videoService from '../services/videoService';
import StatusDropdown from '../components/StatusDropdown/StatusDropdown';
import { useToast } from '../contexts/ToastContext';
import './Feeds.css';

const ESCALATION_OPTIONS = [
    { value: 'CX received empty box', label: 'CX received empty box', tone: 'warning' },
    { value: 'CX received wrong item', label: 'CX received wrong item', tone: 'warning' },
    { value: 'CX received missing item', label: 'CX received missing item', tone: 'warning' }
];

const AUDIT_OPTIONS = [
    { value: 'Pass', label: 'Pass', tone: 'success' },
    { value: 'Fail', label: 'Fail', tone: 'danger' }
];

const OperatorFeeds = () => {
    const toast = useToast();
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
    const [filters, setFilters] = useState({
        awb: '',
        scanType: '',
        status: '',
        startDate: '',
        endDate: ''
    });
    const [playbackVideo, setPlaybackVideo] = useState(null);
    const [playbackUrl, setPlaybackUrl] = useState(null);
    const [loadingPlayback, setLoadingPlayback] = useState(false);

    const fetchVideos = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
            };

            const result = await videoService.getVideos(params);

            if (result.success) {
                setVideos(result.videos);
                setPagination(prev => ({ ...prev, ...result.pagination }));
            }
        } catch (error) {
            console.error('Error fetching videos:', error);
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit, filters]);

    useEffect(() => {
        fetchVideos();
    }, [fetchVideos]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const clearFilters = () => {
        setFilters({
            awb: '',
            scanType: '',
            status: '',
            startDate: '',
            endDate: ''
        });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handlePlayback = async (video) => {
        try {
            setLoadingPlayback(true);
            setPlaybackVideo(video);

            const result = await videoService.getPlaybackUrl(video._id, { action: 'view' });

            if (result.success) {
                setPlaybackUrl(result.playbackUrl);
            }
        } catch (error) {
            console.error('Error getting playback URL:', error);
            toast.error('Failed to load video');
        } finally {
            setLoadingPlayback(false);
        }
    };

    const closePlayback = () => {
        setPlaybackVideo(null);
        setPlaybackUrl(null);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    const handleAnnotationChange = async (video, field, value) => {
        try {
            const result = await videoService.updateAnnotations(video._id, { [field]: value });
            if (result.success) {
                setVideos(prev => prev.map(v => v._id === video._id ? { ...v, [field]: value } : v));
                toast.success('Saved');
            }
        } catch (error) {
            console.error('Error updating annotation:', error);
            toast.error('Failed to save selection');
        }
    };

    return (
        <div className="feeds-page">
            {/* Filters */}
            <div className="filters-section">
                <div className="filters-grid">
                    <div className="filter-group">
                        <label>AWB Number</label>
                        <input
                            type="text"
                            value={filters.awb}
                            onChange={(e) => handleFilterChange('awb', e.target.value)}
                            placeholder="Search AWB..."
                        />
                    </div>

                    <div className="filter-group">
                        <label>Scan Type</label>
                        <select
                            value={filters.scanType}
                            onChange={(e) => handleFilterChange('scanType', e.target.value)}
                        >
                            <option value="">All Types</option>
                            <option value="forward">Forward</option>
                            <option value="backward">Backward</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>Status</label>
                        <select
                            value={filters.status}
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                        >
                            <option value="">All Status</option>
                            <option value="completed">Completed</option>
                            <option value="uploading">Uploading</option>
                            <option value="failed">Failed</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>Start Date</label>
                        <input
                            type="date"
                            value={filters.startDate}
                            onChange={(e) => handleFilterChange('startDate', e.target.value)}
                        />
                    </div>

                    <div className="filter-group">
                        <label>End Date</label>
                        <input
                            type="date"
                            value={filters.endDate}
                            onChange={(e) => handleFilterChange('endDate', e.target.value)}
                        />
                    </div>

                    <div className="filter-group filter-actions">
                        <button className="btn btn-secondary" onClick={clearFilters}>
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Videos Table */}
            <div className="table-section">
                {loading ? (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading videos...</p>
                    </div>
                ) : videos.length === 0 ? (
                    <div className="empty-state">
                        <i className="fas fa-video-slash" style={{ fontSize: '48px', opacity: 0.2, marginBottom: '16px' }}></i>
                        <p>No videos found</p>
                        <p className="empty-hint">Try adjusting your filters or record a new video</p>
                    </div>
                ) : (
                    <>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>AWB Number</th>
                                    <th>Scan Type</th>
                                    <th>Status</th>
                                    <th>Date & Time</th>
                                    <th>Customer Escalation</th>
                                    <th>Audit</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {videos.map(video => (
                                    <tr key={video._id}>
                                        <td className="awb-cell">{video.awb}</td>
                                        <td>
                                            <span className={`scan-badge ${video.scanType}`}>
                                                {video.scanType}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${video.status}`}>
                                                {video.status}
                                            </span>
                                        </td>
                                        <td className="date-cell">{formatDate(video.createdAt)}</td>
                                        <td>
                                            <StatusDropdown
                                                value={video.customerEscalation}
                                                options={ESCALATION_OPTIONS}
                                                placeholder="No escalation"
                                                onChange={(value) => handleAnnotationChange(video, 'customerEscalation', value)}
                                            />
                                        </td>
                                        <td>
                                            <StatusDropdown
                                                value={video.audit}
                                                options={AUDIT_OPTIONS}
                                                placeholder="Not audited"
                                                onChange={(value) => handleAnnotationChange(video, 'audit', value)}
                                            />
                                        </td>
                                        <td>
                                            <button
                                                className="btn btn-sm btn-primary"
                                                onClick={() => handlePlayback(video)}
                                                disabled={video.status !== 'completed'}
                                            >
                                                <i className="fas fa-play" style={{ marginRight: '6px' }}></i> Preview
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        <div className="pagination">
                            <span className="pagination-info">
                                Showing {videos.length} of {pagination.total} videos
                            </span>
                            <div className="pagination-controls">
                                <button
                                    className="btn btn-sm btn-secondary"
                                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                    disabled={pagination.page <= 1}
                                >
                                    Previous
                                </button>
                                <span className="page-info">
                                    Page {pagination.page} of {pagination.pages}
                                </span>
                                <button
                                    className="btn btn-sm btn-secondary"
                                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                    disabled={pagination.page >= pagination.pages}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Video Playback Modal */}
            {playbackVideo && (
                <div className="modal-overlay" onClick={closePlayback}>
                    <div className="modal video-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Video Preview</h2>
                            <button className="close-btn" onClick={closePlayback}>✕</button>
                        </div>
                        <div className="modal-body">
                            <div className="video-info">
                                <span className="info-item">
                                    <strong>AWB:</strong> {playbackVideo.awb}
                                </span>
                                <span className="info-item">
                                    <strong>Type:</strong> {playbackVideo.scanType}
                                </span>
                                <span className="info-item">
                                    <strong>Date:</strong> {formatDate(playbackVideo.createdAt)}
                                </span>
                            </div>
                            {loadingPlayback ? (
                                <div className="video-loading">
                                    <div className="loading-spinner"></div>
                                    <p>Loading video...</p>
                                </div>
                            ) : playbackUrl ? (
                                <video
                                    src={playbackUrl}
                                    controls
                                    autoPlay
                                    className="playback-video"
                                />
                            ) : (
                                <div className="video-error">
                                    <p>Failed to load video</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OperatorFeeds;
